import { Modal } from "flowbite-react"
import { UserContext } from "./contexts"
import { useContext, useState } from "react"
import * as Iconoir from "iconoir-react"
import { settings } from "@/settings"

interface ReferralModalProps {
  show: boolean
  onClose: () => void
}

const ReferralModal = ({ show, onClose }: ReferralModalProps) => {
  const user = useContext(UserContext)
  const [copied, setCopied] = useState(false)
  const referralLink = `${settings.VIBRATO_FRONTEND_URL}/referral?uuid=${user.uuid}`

  const handleCopy = () => {
    navigator.clipboard.writeText(referralLink)
    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 2000)
  }

  return (
    <Modal show={show} onClose={onClose} popup size="lg" className="pt-safe">
      <div className="relative p-6 text-center bg-gradient-to-br from-purple-600 to-indigo-700 rounded-lg">
        <button
          onClick={onClose}
          className="absolute top-2 right-2 p-1 rounded-full text-purple-200 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <Iconoir.Xmark />
        </button>

        <div className="mb-4">
          <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
            <Iconoir.ShareAndroid className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Share the Love!</h2>
          <p className="text-lg text-purple-200 mb-6">
            Invite your friends to Vibrato and you'll both get rewarded with extra call time. It's a win-win for
            everyone!
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center justify-center gap-2">
            <Iconoir.Gift className="w-5 h-5" />
            What you both get:
          </h3>
          <div className="space-y-2 text-purple-100 text-sm">
            <div className="flex items-center justify-center gap-2">
              <Iconoir.CheckCircle className="w-4 h-4 text-green-300" />
              <span>60 extra minutes of call time for you</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Iconoir.CheckCircle className="w-4 h-4 text-green-300" />
              <span>60 extra minutes of call time for your friend</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Iconoir.CheckCircle className="w-4 h-4 text-green-300" />
              <span>Credits are added instantly when they sign up</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Iconoir.CheckCircle className="w-4 h-4 text-green-300" />
              <span>No limit on how many friends you can refer</span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2 bg-white p-2 rounded-lg mb-4">
          <input
            type="text"
            value={referralLink}
            readOnly
            className="w-full p-2 border-none rounded-lg bg-gray-100 text-gray-700 text-sm"
          />
          <button
            onClick={handleCopy}
            className={`p-3 rounded-lg transition-colors flex-shrink-0 ${
              copied ? "bg-green-500 text-white" : "bg-purple-600 text-white hover:bg-purple-700"
            }`}
          >
            {copied ? <Iconoir.Check /> : <Iconoir.Copy />}
          </button>
        </div>

        {copied && (
          <p className="text-white text-sm mb-4 flex items-center justify-center gap-2">
            <Iconoir.CheckCircle className="w-4 h-4" />
            Copied to clipboard!
          </p>
        )}

        <p className="text-purple-200 text-xs">
          Share this link with friends who would love to try Vibrato's AI calling technology.
        </p>
      </div>
    </Modal>
  )
}

export default ReferralModal
